// =============================================================================
// RC EVENTS — the RevenueCat webhook audit + dedupe log. The RC-rail sibling of
// billing_events (billing.ts): append-only, keyed by RevenueCat's own event id,
// so a redelivered webhook (RC retries any non-2xx, and can redeliver the same
// event id) is detected and skipped BEFORE any subscription row is touched —
// the idempotency mechanism IS this table, exactly as billing_events is for
// Stripe. See docs/design/revenuecat-rail-2026-07-24.md.
//
// Kept as its OWN table rather than a `source` column on billing_events
// (FROZEN-foundations: billing_events is the R379 Stripe audit surface,
// untouched) — the two rails keep parallel, independently-queryable audit
// trails, mirroring the "RC is a Source beside Stripe, not a reshape of it" law.
//
// Per-user (userId, text, NULLABLE) — mirrors billing_events.userId exactly:
// the same text-keyed launch-week convention the sibling billing tables use,
// and NULLABLE because an unresolvable app_user_id is logged handled=false,
// never guessed (NO INVENTION). The value stored IS RevenueCat's app_user_id,
// which by contract equals our users.id uuid (stored as text here to match
// billing_events' column type, not re-typed as uuid).
//
// Additive only (rule #11): CREATE TABLE IF NOT EXISTS via the Neon tool,
// mirrored here, exported from index.ts. Nothing dropped or renamed.
// =============================================================================

import { index, jsonb, pgTable, text, varchar } from 'drizzle-orm/pg-core';
import { id, timestamps } from './_shared';

export const rcEvents = pgTable(
  'rc_events',
  {
    id: id(),
    /** RevenueCat's own event id (event.id) — globally unique per RC, so this
     *  IS the idempotency key. A second delivery for the same id is detected by
     *  a SELECT against this column and short-circuited to a 200 without re-
     *  processing (RC treats any non-2xx as "retry later," so idempotent
     *  handling is required, not optional). */
    rcEventId: varchar('rc_event_id', { length: 128 }).notNull(),
    /** The RC event type verbatim (e.g. 'INITIAL_PURCHASE', 'RENEWAL',
     *  'CANCELLATION', 'EXPIRATION', 'BILLING_ISSUE'). Unknown/unhandled types
     *  are still logged here (handled=false) — the audit trail sees everything
     *  RC ever sent, not just what this app currently maps. */
    eventType: varchar('event_type', { length: 64 }).notNull(),
    /** RevenueCat's app_user_id (= our users.id uuid, by contract), or NULL when
     *  the event carried none — NO INVENTION: an unresolvable user is logged,
     *  never guessed. Text-keyed to match billing_events.userId. */
    userId: text('user_id'),
    /** True once this event's side effect (the subscription upsert) actually
     *  ran. False for a duplicate delivery skipped, a type this handler doesn't
     *  map, an unresolvable app_user_id, or a rail-inactive skip. */
    handled: varchar('handled', { length: 8 }).default('false').notNull(),
    payload: jsonb('payload').$type<Record<string, unknown>>(),
    ...timestamps,
  },
  (t) => ({
    eventIdIdx: index('rc_events_event_id_idx').on(t.rcEventId),
    userIdx: index('rc_events_user_idx').on(t.userId),
    typeIdx: index('rc_events_type_idx').on(t.eventType),
  }),
);
